import { GoogleGenAI } from "@google/genai";
import { ComponentType } from "../types";
import { getApiToken } from "../utils/tokenStorage";

const getClient = () => {
  // Check localStorage first, then fall back to environment variable
  const storedKey = getApiToken();
  const apiKey = storedKey || process.env.API_KEY;
  
  if (!apiKey) {
    throw new Error("Gemini API Key is missing. Please configure it in Settings (⚙️ icon in toolbar or press Ctrl+,)");
  }
  return new GoogleGenAI({ apiKey });
}

export const analyzeArchitecture = async (yamlString: string): Promise<string> => {
  try {
    const ai = getClient();
    
    const prompt = `
    You are a Senior System Architect. Analyze the following system architecture defined in YAML.
    
    YAML Definition:
    \`\`\`yaml
    ${yamlString}
    \`\`\`

    Please provide a concise analysis covering:
    1. **Summary**: A brief explanation of what this system likely does.
    2. **Strengths**: What is good about this design.
    3. **Weaknesses/Risks**: Identify single points of failure, bottlenecks, or missing components (e.g., missing cache, missing load balancer).
    4. **Recommendations**: Specific actionable steps to improve it.

    Format the output in clean Markdown.
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
    });

    return response.text || "No analysis could be generated.";
  } catch (error: any) {
    console.error("Gemini API Error:", error);
    
    // Check for authentication errors
    if (error?.message?.includes('API key') || error?.message?.includes('missing')) {
      return "⚠️ **API Key Error**: Please configure your Gemini API key in Settings (⚙️ icon in toolbar or press Ctrl+,)";
    }
    
    if (error?.message?.includes('quota') || error?.message?.includes('429')) {
      return "⚠️ **Quota Exceeded**: You've exceeded your API quota. Please check your Google AI Studio account.";
    }
    
    return `⚠️ **API Error**: Failed to analyze architecture. ${error?.message || 'Unknown error occurred'}`;
  }
};

export const identifySketch = async (base64Image: string): Promise<ComponentType | null> => {
  try {
    const ai = getClient();
    
    // Clean base64 string if it contains the header
    const cleanBase64 = base64Image.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');

    const prompt = `
You are identifying a system architecture component from a rough hand-drawn sketch.
Classify the sketch into exactly one of these component types:
${Object.values(ComponentType).filter(t => t !== 'Group').join(', ')}.

COMPONENT VISUAL GUIDE (matching actual application icons):

DATABASE & STORAGE:
- RDBMS: Cylinder / Drum / Can shape / Stacked cylinders (Database icon - vertical cylinder)
- ObjectStorage: Hard drive / Disk stack / Storage device / Multiple horizontal disks (HardDrive icon)

COMPUTE & SERVICES:
- WebServer: Server stack / Multiple horizontal lines / Server layers / Stacked horizontal rectangles (Server icon)
- Microservice: Simple Rectangle / Box / Square / Generic container (Box icon - most common default)
- ServerlessFunction: Code brackets </> / Angle brackets / Code symbol / Less-than greater-than symbols (Code icon)
- Worker: CPU chip / Processor / Circuit board / Microchip / Square with lines inside (Cpu icon)

NETWORK & ROUTING:
- ApiGateway: Routing paths / Arrows / Network routes / Curved arrows / Path connections (Route icon)
- LoadBalancer: Network nodes / Connected dots / Network diagram / Multiple connected circles / Mesh pattern (Network icon)
- CDN: Globe / World / Earth / Circle with lines / Planet shape (Globe icon)
- Firewall: Shield / Badge / Protective barrier / Upside-down triangle with curved bottom (Shield icon)

DATA & MESSAGING:
- Cache: Lightning bolt / Zigzag / Bolt / Thunder / Jagged line / Flash symbol (Zap icon)
- MessageBroker: Message bubble / Chat bubble / Speech bubble / Rounded rectangle with tail / Cloud with tail (MessageSquare icon)

SEARCH & AUTHENTICATION:
- SearchEngine: Magnifying glass / Search icon / Circle with handle / Loupe / Search tool (Search icon)
- AuthService: Lock / Key / Padlock / Lock symbol / Security lock (Lock icon)

ORCHESTRATION & NOTIFICATIONS:
- Kubernetes: Grid / Layout grid / 3x3 grid pattern / Multiple squares in grid / Matrix pattern (LayoutGrid icon)
- NotificationService: Mail / Envelope / Letter / Rectangle with triangle / Mailbox (Mail icon)

CLASSIFICATION RULES:
1. PRIMARY: Match the main visual shape/icon in the sketch
2. SECONDARY: Consider any text labels (e.g., "API", "DB", "Cache", "Queue")
3. CONTEXT: Consider the component's typical role in architecture diagrams
4. DEFAULTS:
   - Generic box/rectangle without distinguishing features -> Microservice
   - Generic cylinder/drum -> RDBMS
   - Generic cloud shape -> CDN
   - Generic shield -> Firewall
   - Generic globe/circle -> CDN
   - Generic lock -> AuthService
   - Generic mail/envelope -> NotificationService

COMMON SKETCH PATTERNS:
- Lightning/zigzag shape -> Cache
- Network/mesh of connected nodes -> LoadBalancer
- Routing arrows/paths -> ApiGateway
- Code brackets </> -> ServerlessFunction
- CPU/microchip -> Worker
- Server stack/horizontal lines -> WebServer
- Message bubble -> MessageBroker
- Grid of squares -> Kubernetes
- Magnifying glass -> SearchEngine
- Hard drive/disk -> ObjectStorage

Return ONLY the exact component type string (e.g., "WebServer", "RDBMS", "Microservice").
Do not include markdown, explanations, JSON, quotes, or any other text.
    `;

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          { inlineData: { mimeType: 'image/png', data: cleanBase64 } },
          { text: prompt }
        ]
      }
    });

    const text = response.text?.trim().replace(/['"`]/g, '');
    
    // Validate if the response matches a known type (case-insensitive check)
    const match = Object.values(ComponentType).find(type => 
      type.toLowerCase() === text?.toLowerCase()
    );
    
    // Enhanced fallback mappings for common mis-predictions and variations
    if (!match && text) {
      const lowerText = text.toLowerCase();
      
      // Database variations
      if (lowerText.includes('database') || lowerText.includes('db') || lowerText.includes('sql') || lowerText.includes('rdbms')) {
        return ComponentType.DATABASE;
      }
      
      // Server variations
      if (lowerText.includes('server') || lowerText.includes('webserver') || lowerText.includes('web server') || lowerText.includes('http server')) {
        return ComponentType.WEB_SERVER;
      }
      
      // Storage variations
      if (lowerText.includes('storage') || lowerText.includes('bucket') || lowerText.includes('s3') || lowerText.includes('object storage') || lowerText.includes('blob')) {
        return ComponentType.STORAGE;
      }
      
      // Message/Queue variations
      if (lowerText.includes('queue') || lowerText.includes('message') || lowerText.includes('broker') || lowerText.includes('mq') || lowerText.includes('rabbit') || lowerText.includes('kafka')) {
        return ComponentType.MESSAGE_BROKER;
      }
      
      // Cache variations
      if (lowerText.includes('cache') || lowerText.includes('redis') || lowerText.includes('memcached')) {
        return ComponentType.CACHE;
      }
      
      // API Gateway variations
      if (lowerText.includes('api gateway') || lowerText.includes('apigateway') || lowerText.includes('gateway') || lowerText.includes('api-gateway')) {
        return ComponentType.API_GATEWAY;
      }
      
      // Load Balancer variations
      if (lowerText.includes('load balancer') || lowerText.includes('loadbalancer') || lowerText.includes('lb') || lowerText.includes('balancer')) {
        return ComponentType.LOAD_BALANCER;
      }
      
      // CDN variations
      if (lowerText.includes('cdn') || lowerText.includes('content delivery')) {
        return ComponentType.CDN;
      }
      
      // Firewall variations
      if (lowerText.includes('firewall') || lowerText.includes('security') || lowerText.includes('waf')) {
        return ComponentType.FIREWALL;
      }
      
      // Search variations
      if (lowerText.includes('search') || lowerText.includes('elasticsearch') || lowerText.includes('solr')) {
        return ComponentType.SEARCH_ENGINE;
      }
      
      // Auth variations
      if (lowerText.includes('auth') || lowerText.includes('authentication') || lowerText.includes('identity') || lowerText.includes('oauth') || lowerText.includes('jwt')) {
        return ComponentType.AUTH_SERVICE;
      }
      
      // Serverless variations
      if (lowerText.includes('serverless') || lowerText.includes('lambda') || lowerText.includes('function') || lowerText.includes('faas')) {
        return ComponentType.FUNCTION;
      }
      
      // Worker variations
      if (lowerText.includes('worker') || lowerText.includes('background') || lowerText.includes('processor') || lowerText.includes('job')) {
        return ComponentType.WORKER;
      }
      
      // Kubernetes variations
      if (lowerText.includes('kubernetes') || lowerText.includes('k8s') || lowerText.includes('kube') || lowerText.includes('orchestration')) {
        return ComponentType.KUBERNETES;
      }
      
      // Notification variations
      if (lowerText.includes('notification') || lowerText.includes('email') || lowerText.includes('sms') || lowerText.includes('mail')) {
        return ComponentType.NOTIFICATION;
      }
      
      // Microservice variations
      if (lowerText.includes('microservice') || lowerText.includes('service') || lowerText.includes('api') || lowerText.includes('backend')) {
        return ComponentType.MICROSERVICE;
      }
    }

    return match || ComponentType.MICROSERVICE;

  } catch (error: any) {
    console.error("Gemini Vision Error:", error);
    
    // Show alert for authentication errors
    if (error?.message?.includes('API key') || error?.message?.includes('missing')) {
      alert('⚠️ API Key Error: Please configure your Gemini API key in Settings (⚙️ icon in toolbar or press Ctrl+,)');
    }
    
    return ComponentType.MICROSERVICE;
  }
};